
class MultiProductSubscriptions extends HTMLElement {
  constructor() {
    super();
    this.multiProductSubscriptionData = JSON.parse(
      document.getElementById("multi-product-subscription-data").textContent
    );

    this.cart = document.querySelector("cart-drawer");
    this.flushDialog = document.querySelector("#how-many-flush-dialog");

    // GWP (Gift With Purchase) selection state
    this.selectedGwpVariantId = null;
    this.selectedGwpTitle = null;
    this.selectedGwpCampaign = null;
    this.includedGwpInLastRequest = false;

    this.boundVariantSelectorsEventHandler =
      this.variantSelectorsEventHandler.bind(this);
    this.boundProductOptionSelectorEventHandler =
      this.productOptionSelectorEventHandler.bind(this);
    this.boundSubscriptionSelectorsEventHandler =
      this.subscriptionSelectorsEventHandler.bind(this);
    this.boundAddToCart = this.addToCart.bind(this);
    this.boundAutoDiscountUpdatedHandler =
      this.autoDiscountUpdatedHandler.bind(this);
    this.boundPriceTestUpdatedHandler =
      this.priceTestUpdatedHandler.bind(this);
  }

  // Called when the element is inserted into a document.
  connectedCallback() {
    this.section = this;

    // Cache important selectors once
    this._cacheSelectors();

    // Guard clause: make sure required elements exist
    if (!this.subscriptionSelectors || !this.addToCartBtn) {
      console.warn("Missing required elements for MultiProductSubscriptions.");
      return;
    }

    // Register the state-update listener BEFORE reading config so we don't
    // miss an event that fires while connectedCallback is running. Then read
    // current config, with a fallback to the last-emitted detail captured by
    // auto-discount.js for the case where setConfig already emitted before
    // this element upgraded.
    window.addEventListener(
      this.getAutoDiscountUpdatedEventName(),
      this.boundAutoDiscountUpdatedHandler
    );
    this.autoDiscountConfig =
      window.UqoraAutoDiscount?.getLastEmittedConfig?.() ||
      this.getAutoDiscountConfig();
    this.updateAutoDiscountUI();
    this.applyAutoDiscountCode();

    // Price-test (display-only leg): re-render once the cart check resolves,
    // and again on any later cart mutation that could flip the _price_test
    // attribute. Stacks with auto-discount (see getDiscountedPrice).
    window.addEventListener(
      "uqora:price-test-updated",
      this.boundPriceTestUpdatedHandler
    );
    window.UqoraPriceTest?.ready?.then(() => this.loadModule());

    // Parse any variants/subscription types from the URL
    const variantObj = this.getVariantFromURLParam();
    let subscriptionType = this.getSubscriptionFromURLParam();

    // The variant is the strongest signal because it identifies the product
    // Shopify will actually add. Keep copied links from carrying a conflicting
    // base/quarterly variant and cadence pair.
    subscriptionType = this._resolveSubscriptionTypeForVariant(
      variantObj,
      subscriptionType
    );

    // Preselect subscription option if present in URL
    if (subscriptionType) {
      this._selectSubscriptionButton(subscriptionType);
    }

    // Preselect product options if variantObj is present
    if (variantObj) {
      this._preselectOptions(variantObj.handle);
      this.loadModule(variantObj.handle);
      this._filterGalleryByActiveOption();
    } else {
      this.loadModule();
    }

    // Set up all event listeners in one place
    this._initEventListeners();

    // Dispatch a custom event so other scripts can react
    this.dispatchEvent(
      new CustomEvent("MultiProductSubscriptions:connected", {
        bubbles: true,
        cancelable: true,
      })
    );

    // Set up the optional flush dialog trigger
    this._initHowManyFlushDialog();

    // GWP event listeners (store bound handlers for cleanup)
    this._boundGwpSelectedHandler = (e) => {
      this.selectedGwpVariantId = e.detail.variantId;
      this.selectedGwpTitle = e.detail.title;
      this.selectedGwpCampaign = e.detail.campaign || null;
    };
    this._boundGwpDeselectedHandler = () => {
      this.selectedGwpVariantId = null;
      this.selectedGwpTitle = null;
      this.selectedGwpCampaign = null;
    };
    document.addEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.addEventListener("gwp:deselected", this._boundGwpDeselectedHandler);
    this._cartStatus = window.UqoraPdpCartStatus?.connect(
      this, this.addToCartBtn, () => [{ id: this.getVariantId(), selling_plan: this.getSellingPlanId() }],
      { pending: () => this._addToCartPending }
    );
  }

  // Helper to cache all the main elements we need
  _cacheSelectors() {
    this.variantId = document.querySelector("#variant-id");
    this.sellingPlan = document.querySelector("#selling-plan");
    this.variantSelectors = this.section.querySelector("#variant-selectors");
    this.productOptionSelector = this.section.querySelector(
      "#product-option-selector"
    );
    this.subscriptionSelectors = this.section.querySelector(
      "#subscription-selectors"
    );
    this.addToCartBtn = this.section.querySelector(
      "#multi-product-subscriptions--atc"
    );
    this.cartStatus = this.section.querySelector("[data-buybox-cart-status]");
    this.openHowManyFlushDialogBtn = this.querySelector("#how-many-flush");

    this.discountCodeTag = this.section.querySelector(
      "#multi-product-discount-code-tag"
    );
    this.discountCodeTagLabel = this.section.querySelector(
      "[data-discount-code-label]"
    );
    this.firstSubscriptionPaymentNotes = this.section.querySelectorAll(
      "[data-first-subscription-payment]"
    );
  }

  getAutoDiscountUpdatedEventName() {
    return (
      window.UqoraAutoDiscount?.events?.stateUpdated ||
      "uqora:auto-discount-updated"
    );
  }

  getAutoDiscountConfig() {
    if (!window.UqoraAutoDiscount) {
      return {
        enabled: false,
        code: "",
        percentage: 0,
      };
    }

    return window.UqoraAutoDiscount.getConfig();
  }

  // variantId/isSubscription are only needed for the price-test path (fixed-$
  // per-variant amounts). When auto-discount is also active the two stack the
  // way checkout does: the code's percentage is taken from the original price
  // and the price-test amount comes off on top (verified 2026-09-25:
  // $63.75 - 15% ($9.56) - $7.50 = $46.69).
  getDiscountedPrice(price, variantId, isSubscription = false) {
    const amountOff = this.getPriceTestAmount(variantId, isSubscription);

    if (this.autoDiscountConfig.enabled && window.UqoraAutoDiscount) {
      const discounted = window.UqoraAutoDiscount.applyDiscountToPrice(
        price,
        this.autoDiscountConfig
      );
      return amountOff > 0 ? this.roundPrice(discounted - amountOff) : discounted;
    }

    return this.roundPrice(price - amountOff);
  }

  /**
   * Price-test discount amount in DOLLARS for a variant/purchase-type, or 0
   * when price-test is disabled or unconfigured for this variant. It stacks
   * with auto-discount (TRYUQORA, campaign codes); see getDiscountedPrice.
   */
  getPriceTestAmount(variantId, isSubscription) {
    if (!window.UqoraPriceTest || !window.UqoraPriceTest.enabled()) return 0;

    const amountOffCents = window.UqoraPriceTest.amountFor(
      variantId,
      isSubscription
    );
    return amountOffCents > 0 ? amountOffCents / 100 : 0;
  }

  roundPrice(value) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return 0;

    return Math.round((numericValue + Number.EPSILON) * 100) / 100;
  }

  formatPrice(value) {
    const roundedValue = this.roundPrice(value);
    return Number.isInteger(roundedValue)
      ? roundedValue.toFixed(0)
      : roundedValue.toFixed(2);
  }

  applyAutoDiscountCode() {
    if (!this.autoDiscountConfig.enabled || !window.UqoraAutoDiscount) return;

    window.UqoraAutoDiscount.applyDiscountCode(this.autoDiscountConfig.code);
  }

  updateAutoDiscountUI() {
    const isEnabled = Boolean(this.autoDiscountConfig.enabled);

    if (this.discountCodeTag) {
      this.discountCodeTag.hidden = !isEnabled;
      if (this.discountCodeTagLabel) {
        this.discountCodeTagLabel.textContent =
          this.autoDiscountConfig.code || "REGIMEN";
      }
    }

    this.firstSubscriptionPaymentNotes?.forEach((note) => {
      note.hidden = !isEnabled;
    });
  }

  autoDiscountUpdatedHandler(event) {
    const nextConfig = event.detail || this.getAutoDiscountConfig();

    this.autoDiscountConfig = {
      enabled: Boolean(nextConfig.enabled),
      code: nextConfig.code || "",
      percentage: Number(nextConfig.percentage) || 0,
    };

    this.updateAutoDiscountUI();
    this.applyAutoDiscountCode();
    this.loadModule();
  }

  /**
   * Fired when assets/price-test-display.js re-checks the cart's
   * _price_test attribute (initial ready + every cart mutation) and its
   * enabled state may have changed. Re-render with the current selection.
   */
  priceTestUpdatedHandler() {
    this.loadModule();
  }

  // Preselect subscription plan button by URL param
  _selectSubscriptionButton(subscriptionType) {
    const subBtn = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="${subscriptionType}"]`
    );
    if (!subBtn) return false;

    // Unselect all first
    this.subscriptionSelectors
      .querySelectorAll("[data-subscription-selector-selected]")
      .forEach((el) => {
        const isSelected = el === subBtn;
        el.setAttribute(
          "data-subscription-selector-selected",
          String(isSelected)
        );
        const input = el.querySelector('input[type="radio"]');
        if (input) input.checked = isSelected;
      });

    return true;
  }

  _resolveSubscriptionTypeForVariant(variantObj, subscriptionType) {
    if (!variantObj) return subscriptionType;

    const isMultiProductVariant =
      this.multiProductSubscriptionData.multiProductSubscription.variants.some(
        (variant) => String(variant.id) === String(variantObj.id)
      );
    if (isMultiProductVariant) return "multi-product";
    return subscriptionType === "one-time-purchase"
      ? "one-time-purchase"
      : "default-selling-plan";
  }

  // Preselect product option buttons by handle
  _preselectOptions(handle) {
    if (!this.productOptionSelector) return;
    const selectorsContainers = this.productOptionSelector.querySelectorAll(
      ".selectors-container"
    );

    selectorsContainers.forEach((container) => {
      const buttons = container.querySelectorAll("button[data-option-handle]");
      buttons.forEach((btn) => {
        const optionHandle = btn.dataset.optionHandle;
        // Use substring match to allow multi-word/multi-part options
        if (handle.includes(optionHandle)) {
          btn.setAttribute("data-variant-selected", "true");
          btn.setAttribute("aria-pressed", "true");
        } else {
          btn.setAttribute("data-variant-selected", "false");
          btn.setAttribute("aria-pressed", "false");
        }
      });
    });
  }

  // Filter media gallery by currently selected product option, if present
  _filterGalleryByActiveOption() {
    if (!this.productOptionSelector) return;
    const activeProductOptionSelector =
      this.productOptionSelector.querySelector(
        `.selectors-container button[data-variant-selected="true"]`
      );
    if (activeProductOptionSelector) {
      this.filterMediaGalleryByVariant(
        activeProductOptionSelector.dataset.optionHandle
      );
    }
  }

  // Group all event listeners here for easy scaling and clean-up
  _initEventListeners() {
    if (this.variantSelectors) {
      this.variantSelectors.addEventListener(
        "click",
        this.boundVariantSelectorsEventHandler
      );
    }

    if (this.productOptionSelector) {
      this.productOptionSelector.addEventListener(
        "click",
        this.boundProductOptionSelectorEventHandler
      );
    }

    this.subscriptionSelectors.addEventListener(
      "click",
      this.boundSubscriptionSelectorsEventHandler
    );

    this.addToCartBtn.addEventListener("click", this.boundAddToCart);
  }

  // Set up the "How many flush" dialog button if present
  _initHowManyFlushDialog() {
    if (this.openHowManyFlushDialogBtn && this.flushDialog) {
      this.openHowManyFlushDialogBtn.addEventListener("click", () => {
        this.flushDialog.show();
      });
    }
  }

  // Called when the element is removed from a document.
  disconnectedCallback() {
    this._cartStatus?.disconnect();
    if (this.productOptionSelector) {
      this.productOptionSelector.removeEventListener(
        "click",
        this.boundProductOptionSelectorEventHandler
      );
    }

    if (this.variantSelectors) {
      this.variantSelectors.removeEventListener(
        "click",
        this.boundVariantSelectorsEventHandler
      );
    }

    if (this.subscriptionSelectors) {
      this.subscriptionSelectors.removeEventListener(
        "click",
        this.boundSubscriptionSelectorsEventHandler
      );
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.removeEventListener("click", this.boundAddToCart);
    }

    window.removeEventListener(
      this.getAutoDiscountUpdatedEventName(),
      this.boundAutoDiscountUpdatedHandler
    );
    window.removeEventListener(
      "uqora:price-test-updated",
      this.boundPriceTestUpdatedHandler
    );

    // Clean up GWP event listeners
    document.removeEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.removeEventListener("gwp:deselected", this._boundGwpDeselectedHandler);
  }

  /**
   * Handles click events on variant selectors.
   * @param {Event} event - The click event.
   */
  variantSelectorsEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;
    const { handle } = currentSelectedBtn.dataset;
    const previousSelectedBtn = this.variantSelectors.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule(handle);
  }

  productOptionSelectorEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;

    const selectorsContainer = currentSelectedBtn.closest(
      ".selectors-container"
    );
    const previousSelectedBtn = selectorsContainer.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule();
    const { optionHandle } = currentSelectedBtn.dataset;
    this.filterMediaGalleryByVariant(optionHandle);
  }

  /**
   * Handles click events on subscription selectors.
   * @param {Event} event - The click event.
   */
  subscriptionSelectorsEventHandler(event) {
    const currentSelectedSubscription = event.target.closest(
      ".subscription-selector"
    );
    if (!currentSelectedSubscription) return false;
    this._selectSubscriptionButton(
      currentSelectedSubscription.dataset.subscriptionSelectorType
    );

    this.updateSubscriptionTagContent(currentSelectedSubscription);

    this.loadModule();
  }

  /**
   * Toggles the visibility of media carousels based on the selected variant.
   * @param {string} handle - The handle of the variant selector.
   */
  filterMediaGalleryByVariant(handle) {
    const mediaGalleryElm = document.querySelector(".mediaGallery-container");
    const altTextList = mediaGalleryElm?.getAttribute("data-alt-text-list");
    if (mediaGalleryElm && altTextList.indexOf(handle) > -1) {
      mediaGalleryElm.setAttribute("data-active-variant-handle", handle);
    }
  }

  /**
   * Loads the module with the specified handle.
   * @param {string} handle - The handle of the variant selector.
   */
  loadModule(handle = "") {
    const currentSelectedVariantHandle =
      handle || this.getSelectedVariantSelectorHandle();

    let productVariant = this.getProductVariantByHandle(
      currentSelectedVariantHandle
    );
    let multiProductVariant;

    if (productVariant) {
      multiProductVariant = this.getMultiProductVariantByHandleOrSku(
        currentSelectedVariantHandle,
        productVariant.sku
      );
    } else {
      // A copied quarterly URL can point directly at the companion product's
      // variant (for example, Defend's 3-bottle SKU). Resolve the base product
      // by the shared handle or the established `-90` SKU convention so the
      // selling plan and price state can still initialize normally.
      multiProductVariant =
        this.multiProductSubscriptionData.multiProductSubscription.variants.find(
          (variant) => variant.handle === currentSelectedVariantHandle
        );
      if (multiProductVariant) {
        productVariant =
          this.multiProductSubscriptionData.product.variants.find(
            (variant) =>
              variant.handle === multiProductVariant.handle ||
              multiProductVariant.sku === `${variant.sku}-90`
          ) ||
          (this.multiProductSubscriptionData.product.variants.length === 1
            ? this.multiProductSubscriptionData.product.variants[0]
            : null);
      }
    }

    if (!productVariant || !multiProductVariant) return;

    const subscriptionSelected = this.getSelectedSubscription();

    this.updateVariantAndSellingPlanId(
      subscriptionSelected,
      productVariant,
      multiProductVariant
    );

    this.updateSubscriptionSelectorsPrices(
      productVariant,
      multiProductVariant,
      subscriptionSelected
    );
    this._cartStatus?.sync();
  }

  /**
   * Updates the variant ID and selling plan ID based on subscription selection.
   * @param {string} subscriptionSelected - The selected subscription type.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   */
  updateVariantAndSellingPlanId(
    subscriptionSelected,
    productVariant,
    multiProductVariant
  ) {
    let variantIdToSet = null;
    switch (subscriptionSelected) {
      case "multi-product": {
        this.variantId.value = multiProductVariant.id;
        this.sellingPlan.value = this.getMultiProductSellingPlanId();
        variantIdToSet = multiProductVariant.id;
        break;
      }
      case "default-selling-plan": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = this.getProductSellingPlanId();
        variantIdToSet = productVariant.id;
        break;
      }
      case "one-time-purchase": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = "";
        variantIdToSet = productVariant.id;
        break;
      }
      default:
        break;
    }
    if (variantIdToSet) {
      const subscriptionType = this.getSelectedSubscription();
      this.updateURLWithVariantAndSubscription(
        variantIdToSet,
        subscriptionType
      );
    }
  }

  /**
   * Updates the prices displayed on subscription selectors.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   * @param {String} subscriptionSelected - Currently selected subscription
   */
  updateSubscriptionSelectorsPrices(
    productVariant,
    multiProductVariant,
    subscriptionSelected
  ) {
    const {
      sellingPlanPrice: multiProductVariantSellingPlanPrice,
      id: multiProductVariantId,
    } = multiProductVariant;

    const productDefaultSellingPlan = this.getDefaultSellingPlan();
    const productRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        productDefaultSellingPlan.frequencyNumber,
        productDefaultSellingPlan.frequencyPeriod
      );

    const multiProductSellingPlan = this.getMultiProductSellingPlan();
    const multiProductRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        multiProductSellingPlan.frequencyNumber,
        multiProductSellingPlan.frequencyPeriod
      );

    const {
      sellingPlanPrice: productVariantSellingPlanPrice,
      price: productVariantPrice,
      comparePrice: productVariantComparePrice,
      id: productVariantId,
    } = productVariant;

    const discountedProductVariant = {
      ...productVariant,
      price: this.getDiscountedPrice(productVariantPrice, productVariantId, false),
      sellingPlanPrice: this.getDiscountedPrice(
        productVariantSellingPlanPrice,
        productVariantId,
        true
      ),
    };

    const discountedMultiProductVariant = {
      ...multiProductVariant,
      sellingPlanPrice: this.getDiscountedPrice(
        multiProductVariantSellingPlanPrice,
        multiProductVariantId,
        true
      ),
    };

    // Renewal prices for the auto-discount "after first payment" notes: the
    // code only discounts the first order, the price-test amount persists.
    const multiProductRenewalPrice = this.roundPrice(
      multiProductVariantSellingPlanPrice -
        this.getPriceTestAmount(multiProductVariantId, true)
    );
    const productRenewalPrice = this.roundPrice(
      productVariantSellingPlanPrice - this.getPriceTestAmount(productVariantId, true)
    );

    this.setSubscriptionSelectorPrices(
      discountedMultiProductVariant.sellingPlanPrice,
      multiProductRecurrenceAndFrequency,
      multiProductVariantId,
      productVariantPrice,
      productVariantComparePrice,
      multiProductRenewalPrice
    );

    this.setDefaultSellingPlanPrices(
      discountedProductVariant.sellingPlanPrice,
      discountedProductVariant.price,
      productRecurrenceAndFrequency,
      productVariantId,
      productVariantComparePrice,
      productVariantPrice,
      productRenewalPrice
    );

    // Precedence: auto-discount strikethrough > price-test strikethrough (raw
    // undiscounted one-time price) > null (falls back to Shopify compare-at).
    let oneTimeOriginalPrice = null;
    if (this.autoDiscountConfig.enabled) {
      oneTimeOriginalPrice = productVariant.strikethroughPrice;
    } else if (this.getPriceTestAmount(productVariantId, false) > 0) {
      oneTimeOriginalPrice = productVariantPrice;
    }

    this.setProductVariantPrice(
      discountedProductVariant.price,
      productVariantComparePrice,
      productVariantId,
      oneTimeOriginalPrice
    );

    const event = new CustomEvent("multi-product-subscription::update-prices", {
      detail: {
        subscriptionSelected,
        productVariant: discountedProductVariant,
        productRecurrenceAndFrequency,
        multiProductVariant: discountedMultiProductVariant,
        multiProductRecurrenceAndFrequency,
        autoDiscount: this.autoDiscountConfig,
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Updates the content of the subscription tag with the savings percentage.
   * If no savings percentage or subscription tag is found, hides all subscription tags.
   *
   * Note: Hiding all subscription tags is a temporary solution
   * until the Intelligems test is completed. Ideally, there should
   * be only one element of this type.
   *
   * @param {HTMLElement} currentSelectedSubscription Subscription selector
   */
  updateSubscriptionTagContent(currentSelectedSubscription) {
    const savingsPercentage =
      currentSelectedSubscription.querySelector(".savings-pill")?.dataset
        .percentage;
    const subscriptionTag = this.querySelector(".subscription-tag");

    if (!savingsPercentage || savingsPercentage === "0") {
      subscriptionTag.style.display = "none";
      return;
    }
    subscriptionTag.style.display = "block";
    subscriptionTag.innerHTML = `Save ${savingsPercentage}%`;
  }

  /**
   * Adds the selected product to the cart.
   */
  setCartStatus(message, isError = false) {
    if (!this.cartStatus) return;
    this.cartStatus.textContent = message || "";
    this.cartStatus.dataset.status = isError ? "error" : "info";
    this.cartStatus.hidden = !message;
  }

  async addToCart() {
    if (this._addToCartPending) return;
    this._addToCartPending = true;
    const originalLabel = this.addToCartBtn.innerHTML;
    let cartCommitted = false;
    let gwpClaimed = false;
    const claimGwp = () => {
      if (!this.includedGwpInLastRequest || gwpClaimed) return;
      gwpClaimed = true;
      try {
        document.dispatchEvent(new CustomEvent("gwp:claimed"));
      } finally {
        this.selectedGwpVariantId = null;
        this.selectedGwpTitle = null;
        this.selectedGwpCampaign = null;
      }
    };

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = "ADDING TO THE CART";
      this.setCartStatus("");

      // Wait for any in-flight discount apply/clear to land before adding the
      // item, so the cart's discount state is consistent at /cart/add.js time.
      if (window.UqoraAutoDiscount?.pendingCartUpdate) {
        try { await window.UqoraAutoDiscount.pendingCartUpdate; } catch (_) {}
      }
      if (window.UqoraCampaignOffers?.getActiveOffer?.()) {
        const campaignReady = await window.UqoraCampaignOffers.ensureCartAttribute();
        if (!campaignReady) {
          this.setCartStatus("We could not prepare this offer. Please try again.", true);
          return;
        }
      }

      const formData = this.buildFormDataForRequest();
      const variantId = this.getVariantId();

      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!request.ok) {
        const errorText = await request.text();
        throw new Error(`cart/add.js failed: ${request.status} ${errorText}`);
      }

      cartCommitted = true;
      let response = await request.json();

      // The item is already committed at this point. Claim GWP state exactly
      // once even if discount verification or cart-drawer rendering fails.
      claimGwp();

      let discountWarning = false;
      if (
        this.autoDiscountConfig.enabled &&
        window.UqoraAutoDiscount?.ensureDiscountCodeApplied
      ) {
        const verification = await window.UqoraAutoDiscount.ensureDiscountCodeApplied(
          this.autoDiscountConfig.code,
          {
            sections: formData.sections || [],
            sectionsUrl: formData.sections_url || window.location.pathname,
          }
        );
        if (verification.cart) response = verification.cart;
        discountWarning = !verification.ok;
      }

      let uiSyncFailed = false;
      try {
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: variantId,
            cartData: response,
          });
        }
      } catch (error) {
        uiSyncFailed = true;
        console.error("Multi Product Subscription | cart publish failed", error);
      }

      try {
        this.cart?.renderContents?.(response);
      } catch (error) {
        uiSyncFailed = true;
        console.error("Multi Product Subscription | cart render failed", error);
      }

      if (discountWarning) {
        this.setCartStatus(
          "Added to cart, but the promotional discount could not be applied. Review your cart.",
          true
        );
      } else if (uiSyncFailed) {
        this.setCartStatus("Added to cart. Refresh to view the updated cart.", true);
      } else {
        this.setCartStatus("Added to cart.");
      }
    } catch (error) {
      if (cartCommitted) {
        claimGwp();
        console.error("Multi Product Subscription | post-add sync failed", error);
        this.setCartStatus("Added to cart. Refresh to view the updated cart.", true);
      } else {
        console.error("Multi Product Subscription | addToCart failed", error);
        this.setCartStatus("We couldn't add this item to your cart. Please try again.", true);
        if (typeof hj === "function") {
          hj("event", "error_occurred");
        }
      }
    } finally {
      this._addToCartPending = false;
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = originalLabel;
      this._cartStatus?.sync();
      if (cartCommitted) window.UqoraPdpCartStatus?.refresh();
      if (cartCommitted && this.cart?.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }
    }
  }

  /**
   * Retrieves the selected subscription type.
   * @returns {string} The selected subscription type.
   */
  getSelectedSubscription() {
    return this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-selected="true"]`
    )?.dataset.subscriptionSelectorType || "default-selling-plan";
  }

  /**
   * Retrieves the handle of the selected variant selector.
   * @returns {string} The handle of the selected variant selector.
   */
  getSelectedVariantSelectorHandle() {
    if (this.variantSelectors) {
      return this.variantSelectors.querySelector(
        `[data-variant-selected="true"]`
      ).dataset.optionHandle;
    }

    if (this.productOptionSelector) {
      return Array.from(
        this.productOptionSelector.querySelectorAll(
          `[data-variant-selected="true"]`
        )
      )
        .map((el) => el.dataset.optionHandle)
        .join("-");
    }
  }

  /**
   * Retrieves the product variant by handle.
   * @param {string} handle - The handle of the product variant.
   * @returns {Object} The product variant object.
   */
  getProductVariantByHandle(handle) {
    return this.multiProductSubscriptionData.product.variants.find(
      (el) => el.handle === handle
    );
  }

  /**
   * Retrieves the multi-product variant by handle, or if there's no handle match it tries to compare the SKU of the normal product with the SKU of the 3 month supply using the suffix "-90".
   * @param {string} handle - The handle of the multi-product variant.
   * @param {string} sku - The sku of the multi-product variant.
   * @returns {Object} The multi-product variant object.
   */
  getMultiProductVariantByHandleOrSku(handle, sku) {
    return this.multiProductSubscriptionData.multiProductSubscription.variants.find(
      (el) => el.handle === handle || el.sku === sku + "-90"
    );
  }

  /**
   * Retrieve the product's selling plan object
   * @returns {object} Selling plan object
   */
  getDefaultSellingPlan() {
    return this.multiProductSubscriptionData.product.sellingPlan;
  }

  /**
   * Retrieve the multi product's selling plan object
   * @returns {object} Selling plan object
   */
  getMultiProductSellingPlan() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan;
  }

  /**
   * Retrieves the selling plan ID for multi-product subscription.
   * @returns {number} The selling plan ID for multi-product subscription.
   */
  getMultiProductSellingPlanId() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan.id;
  }

  /**
   * Retrieves the selling plan ID for the default product.
   * @returns {number} The selling plan ID for the default product.
   */
  getProductSellingPlanId() {
    return this.multiProductSubscriptionData.product.sellingPlan.id;
  }

  /**
   * Retrieves the variant ID.
   * @returns {number} The variant ID.
   */
  getVariantId() {
    return +this.variantId.value;
  }

  /**
   * Retrieves the selling plan ID.
   * @returns {number} The selling plan ID.
   */
  getSellingPlanId() {
    return +this.sellingPlan.value;
  }

  /**
   * Sets the prices for subscription selectors for multi-product subscription.
   * @param {number} multiProductVariantSellingPlanPrice - The selling plan price for multi-product variant.
   * @param {Object} multiProductRecurrenceAndFrequency Selling plan object
   * @param {number} productVariantPrice - The price for product variant.
   */
  setSubscriptionSelectorPrices(
    multiProductVariantSellingPlanPrice,
    multiProductRecurrenceAndFrequency,
    multiProductVariantId,
    productVariantPrice,
    productVariantComparePrice,
    originalSellingPlanPrice
  ) {
    const { pattern, months, weeks } = multiProductRecurrenceAndFrequency;
    const unitLabel = this.multiProductSubscriptionData.unitLabel || '';
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="multi-product"]`
    );

    // Monthly equivalent price — total bundle price divided by the number of months.
    // Used both for display and for the combined savings percentage calculation below.
    const monthlyEquivalentPrice =
      multiProductVariantSellingPlanPrice / Math.max(months, 1);
    const monthlyPrice = this.formatPrice(monthlyEquivalentPrice);

    // Only the auto-discount arm swaps "/mo" for the unit label. The price test
    // keeps the control's "/mo" so its arms differ only in price; the longer
    // "/bottle" label also wrapped the 3-month title on Defend and Promote,
    // shifting the box 35px when the price test resolved.
    const priceLabel = this.autoDiscountConfig.enabled
      ? (months > 1 && unitLabel ? ` /${unitLabel}` : '')
      : ' /mo';
    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${monthlyPrice}${priceLabel}`;

    // .default-price preserves the control-arm cadence helper. In the
    // auto-discount arm, multi-month renewals use the same first-payment note
    // element and styling as the monthly option, which needs the full
    // recurring amount. Price-test discounts persist every cycle, so outside
    // auto-discount "paid {pattern}" reflects the discounted price, same as
    // .selling-plan-price above.
    const undiscountedTotal = this.autoDiscountConfig.enabled
      ? (originalSellingPlanPrice ?? multiProductVariantSellingPlanPrice)
      : multiProductVariantSellingPlanPrice;
    const defaultPriceEl = selector.querySelector(".default-price");
    const showMultiMonthFirstPaymentNote =
      months > 1 && this.autoDiscountConfig.enabled;
    if (months > 1 && !showMultiMonthFirstPaymentNote) {
      defaultPriceEl.style.removeProperty('display');
      defaultPriceEl.innerHTML = `$<span>${this.formatPrice(undiscountedTotal)}</span> paid ${pattern}`;
    } else {
      defaultPriceEl.style.display = 'none';
    }

    // Use one visual contract for every renewal cadence in the auto-discount
    // arm. Quarterly copy intentionally omits "paid" per the reviewed design.
    const note = selector.querySelector('[data-first-subscription-payment]');
    if (note) {
      if (showMultiMonthFirstPaymentNote) {
        note.innerHTML = `$${this.formatPrice(undiscountedTotal)} ${pattern} after first payment`;
        note.hidden = false;
      } else if ((months === 1 || weeks === 4) && this.autoDiscountConfig.enabled) {
        note.innerHTML = `$${this.formatPrice(undiscountedTotal)}/mo after first payment`;
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }

    // Use variant compare price of one-time purchase product, if empty then use one-time purchase price
    selector.querySelector(
      ".selling-plan-compare-price"
    ).innerHTML = `$${
      productVariantComparePrice !== 0
        ? this.formatPrice(productVariantComparePrice)
        : this.formatPrice(productVariantPrice)
    }`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", multiProductVariantId);

    // Use the client-approved treatment savings percentage when configured;
    // otherwise calculate it from the displayed subscription price.
    const basePrice =
      productVariantComparePrice !== 0 ? productVariantComparePrice : productVariantPrice;
    if (basePrice > 0 && monthlyEquivalentPrice > 0) {
      const priceTestSavings =
        this.autoDiscountConfig.enabled ||
        this.getPriceTestAmount(multiProductVariantId, true) <= 0
        ? null
        : window.UqoraPriceTest?.savingsPercentFor?.(
            this.multiProductSubscriptionData.productHandle,
            "3-month"
          );
      const combinedSavingsPct = Number.isFinite(priceTestSavings)
        ? priceTestSavings
        : Math.round(
            ((basePrice - monthlyEquivalentPrice) / basePrice) * 100
          );
      const savingsPill = selector.querySelector(".savings-pill");
      if (savingsPill) {
        const pct = combinedSavingsPct > 0 ? String(combinedSavingsPct) : "0";
        savingsPill.setAttribute("data-percentage", pct);
        savingsPill.innerHTML = combinedSavingsPct > 0 ? `Save ${combinedSavingsPct}%` : "";
      }
    }

    // If this selector is currently selected, sync the subscription tag text.
    if (selector.dataset.subscriptionSelectorSelected === "true") {
      this.updateSubscriptionTagContent(selector);
    }
  }

  /**
   * Sets the prices for subscription selectors for default product.
   * @param {number} productVariantSellingPlanPrice - The selling plan price for product variant.
   * @param {number} productVariantPrice - The price for product variant.
   * @param {Object} productRecurrenceAndFrequency Selling plan object
   */
  setDefaultSellingPlanPrices(
    productVariantSellingPlanPrice,
    productVariantPrice,
    productRecurrenceAndFrequency,
    productVariantId,
    productVariantComparePrice,
    originalPrice,
    originalSellingPlanPrice
  ) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="default-selling-plan"]`
    );
    const { pattern, months } = productRecurrenceAndFrequency;

    const fixedProductVariantSellingPlanPrice = this.formatPrice(
      productVariantSellingPlanPrice
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${fixedProductVariantSellingPlanPrice}${this.autoDiscountConfig.enabled ? '' : ' /mo'}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);

    selector.querySelector(
      ".default-price"
    ).innerHTML = `$<span data-variant-id="${productVariantId}">${fixedProductVariantSellingPlanPrice}</span> paid ${pattern}`;

    // If both the subscription and one-time price are the same, hide the compare price.
    // Otherwise show Shopify's compare-at price, or fall back to originalPrice (pre-auto-discount).
    if (this.roundPrice(productVariantSellingPlanPrice) === this.roundPrice(productVariantPrice)) {
      selector.querySelector(".selling-plan-compare-price").innerHTML = "";
    } else {
      selector.querySelector(
        ".selling-plan-compare-price"
      ).innerHTML = `$${
        productVariantComparePrice !== 0
          ? this.formatPrice(productVariantComparePrice)
          : this.formatPrice(originalPrice ?? productVariantPrice)
      }`;
    }

    // Use the client-approved treatment savings percentage when configured;
    // otherwise calculate it from the displayed subscription price.
    const basePrice =
      productVariantComparePrice !== 0
        ? productVariantComparePrice
        : (originalPrice ?? productVariantPrice);
    if (basePrice > 0 && productVariantSellingPlanPrice > 0) {
      const priceTestSavings =
        this.autoDiscountConfig.enabled ||
        this.getPriceTestAmount(productVariantId, true) <= 0
        ? null
        : window.UqoraPriceTest?.savingsPercentFor?.(
            this.multiProductSubscriptionData.productHandle,
            "1-month"
          );
      const combinedSavingsPct = Number.isFinite(priceTestSavings)
        ? priceTestSavings
        : Math.round(
            ((basePrice - productVariantSellingPlanPrice) / basePrice) * 100
          );
      const savingsPill = selector.querySelector(".savings-pill");
      if (savingsPill) {
        const pct = combinedSavingsPct > 0 ? String(combinedSavingsPct) : "0";
        savingsPill.setAttribute("data-percentage", pct);
        savingsPill.innerHTML = combinedSavingsPct > 0 ? `Save ${combinedSavingsPct}%` : "";
      }
    }

    // Always show the first-payment note on the default-selling-plan tile.
    // When auto-discount is active and cadence is monthly, set the discounted amount;
    // otherwise fall back to the generic "first subscription payment" label.
    const dspNote = selector.querySelector('[data-first-subscription-payment]');
    if (dspNote) {
      if (this.autoDiscountConfig.enabled && originalSellingPlanPrice && months === 1) {
        dspNote.innerHTML = `$${this.formatPrice(originalSellingPlanPrice)}/mo after first payment`;
        dspNote.hidden = false;
      } else {
        dspNote.hidden = true;
      }
    }

    // If this selector is currently selected, sync the subscription tag text.
    if (selector.dataset.subscriptionSelectorSelected === "true") {
      this.updateSubscriptionTagContent(selector);
    }
  }

  /**
   * Sets the price for product variant in one-time purchase subscription.
   * @param {number} price - The price for product variant.
   * @param {number} comparePrice - The compare price for product variant.
   */
  setProductVariantPrice(price, comparePrice, productVariantId, originalPrice) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="one-time-purchase"]`
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${this.formatPrice(price)}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);

    // originalPrice is only ever set by the caller when auto-discount OR
    // price-test is actively discounting this variant's one-time price.
    // Otherwise fall back to Shopify's compare-at price when present.
    let oneTimeComparePrice = "";
    if (originalPrice != null) {
      oneTimeComparePrice = `$${this.formatPrice(originalPrice)}`;
    } else if (comparePrice != 0) {
      oneTimeComparePrice = `$${this.formatPrice(comparePrice)}`;
    }
    selector.querySelector(".selling-plan-compare-price").innerHTML = oneTimeComparePrice;
  }

  /**
   * Calculates the recurrence pattern based on the frequency number and period.
   * @param {number} recurrenceCount - The number of times the event repeats.
   * @param {string} recurrenceUnit - The unit of recurrence, either "week" or "day".
   * @returns {Object} The recurrence pattern.
   */
  calculateRecurrencePatternAndFrequency(frequencyNumber, frequencyPeriod) {
    const weeksInMonth = 4;
    const averageDaysInAMonth = 30.437;
    let pattern = "";
    let months = 0;
    let weeks = 0;

    const determineMonthlyPattern = (monthsCount) => {
      if (monthsCount === 1) return "monthly";
      if (monthsCount === 3) return "quarterly";
      return monthsCount > 1 ? `every ${monthsCount} months` : "monthly";
    };

    const determineWeeklyPattern = () => {
      return frequencyNumber > 1 ? `every ${frequencyNumber} weeks` : "weekly";
    };

    if (frequencyPeriod === "week") {
      if (frequencyNumber < weeksInMonth) {
        pattern = determineWeeklyPattern(frequencyNumber);
        weeks = frequencyNumber;
        return { pattern, months, weeks };
      }

      months = frequencyNumber / weeksInMonth;
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }

    if (frequencyPeriod === "day") {
      if (frequencyNumber < averageDaysInAMonth) {
        pattern = "monthly";
        months = 1;
        return { pattern, months, weeks };
      }

      months = Math.round(frequencyNumber / averageDaysInAMonth);
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }

    return { pattern: "monthly", months: 1, weeks: 0 };
  }

  generateBundleId() {
    return `gwp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  buildFormDataForRequest() {
    const sellingPlanId = this.getSellingPlanId();

    const items = [
      {
        id: this.getVariantId(),
        quantity: 1,
        ...(sellingPlanId && {
          selling_plan: sellingPlanId,
        }),
      },
    ];

    const giftResult = window.UqoraCampaignOffers?.appendGiftLine?.({
      items,
      productHandle: this.multiProductSubscriptionData.productHandle,
      selectedVariantId: this.selectedGwpVariantId,
      selectedCampaign: this.selectedGwpCampaign,
      createBundleId: () => this.generateBundleId(),
    });
    this.includedGwpInLastRequest = Boolean(giftResult?.included);

    const formData = { items };
    if (this.cart?.getSectionsToRender) {
      formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
      formData.sections_url = window.location.pathname;
    }
    return formData;
  }

  /**
   * Updates the URL to reflect the given variant ID and subscription type.
   * @param {number|string} variantId
   * @param {string} subscriptionType
   */
  updateURLWithVariantAndSubscription(variantId, subscriptionType) {
    if (!this.isLegacyBuyBoxActive()) return;

    const url = new URL(window.location);
    url.searchParams.set("variant", variantId);
    url.searchParams.set("subscription", subscriptionType);
    window.history.replaceState({}, "", url);
  }

  /**
   * Returns whether the legacy buy box owns URL updates on this page.
   * During VWO #134 both buy boxes are mounted, but only the visible arm may
   * write the selected variant or the two controllers can overwrite each other.
   */
  isLegacyBuyBoxActive() {
    const managedUnifiedBuyBox = document.querySelector(
      "uq-buybox[data-uq-ab-managed]"
    );
    if (!managedUnifiedBuyBox) return true;

    const qaArm = document.body?.getAttribute("data-uq-buybox-qa");
    if (qaArm === "legacy") return true;
    if (qaArm === "new") return false;

    return !document.body?.classList.contains("vwo-new-buybox");
  }

  /**
   * Gets the variant object from the URL 'variant' param, or null if not found/invalid.
   * @returns {Object|null} The variant object or null.
   */
  getVariantFromURLParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get("variant");
    if (variantParam) {
      const variantId = parseInt(variantParam, 10);

      // Search in product variants
      const productVariant = this.multiProductSubscriptionData.product.variants.find(
        (v) => v.id === variantId
      );
      if (productVariant) return productVariant;

      // Search in multiProductSubscription variants
      const multiProductVariant =
        this.multiProductSubscriptionData.multiProductSubscription.variants.find(
          (v) => v.id === variantId
        );
      if (multiProductVariant) return multiProductVariant;

      // A one-variant base product can still have a distinct three-month
      // variant. Only fall back to the base variant after checking both
      // product collections for an exact URL match.
      if (this.multiProductSubscriptionData.product.variants.length === 1) {
        return this.multiProductSubscriptionData.product.variants[0];
      }

      return null;
    }
    return null;
  }

  /**
   * Gets the subscription type from the URL param, or null if not present.
   * @returns {string|null}
   */
  getSubscriptionFromURLParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionType = urlParams.get("subscription");
    if (!subscriptionType) return null;

    const supportedTypes = [
      "multi-product",
      "default-selling-plan",
      "one-time-purchase",
    ];
    if (!supportedTypes.includes(subscriptionType)) return null;

    return this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="${subscriptionType}"]`
    )
      ? subscriptionType
      : null;
  }
}

customElements.define("multi-product-subscriptions", MultiProductSubscriptions);
